import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ShieldAlert } from "lucide-react";

export default function CRMLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const denied = searchParams.get("denied") === "1";

  useEffect(() => {
    if (!loading && user) {
      navigate("/crm", { replace: true });
    }
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/crm";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[color:var(--hs-bg-0)] grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 section-depth grid-lines border-r border-white/10">
        <img src="/logo.png" alt="Holdstill Records" className="h-14 w-auto self-start" />
        <div>
          <h1 className="font-display uppercase text-5xl xl:text-6xl text-white leading-[0.95]">
            The <span className="text-[color:var(--hs-lime)]">growth</span>
            <br />
            engine room
          </h1>
          <p className="mt-5 max-w-md text-[color:var(--hs-ink-dim)]">
            Every lead from the website, every follow-up, every conversation — tracked in one place
            for the Holdstill team.
          </p>
        </div>
        <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-white/40">
          Holdstill Records · Internal CRM
        </p>
      </div>

      {/* Login card */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[color:var(--hs-bg-1)] p-8 sm:p-10 shadow-[var(--shadow-elev-2)]">
          <img src="/logo.png" alt="Holdstill Records" className="h-10 w-auto lg:hidden mb-6" />
          <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-lime)]">Team access</p>
          <h2 className="mt-3 font-display uppercase text-3xl text-white">Sign in to CRM</h2>
          <p className="mt-3 text-sm text-[color:var(--hs-ink-dim)]">
            Use your Google account to access leads, follow-ups and client conversations.
          </p>
          {denied && (
            <div
              className="mt-5 flex items-start gap-2.5 rounded-xl border border-[rgba(255,120,120,0.3)] bg-[rgba(255,120,120,0.08)] p-3.5"
              data-testid="crm-access-denied-alert"
            >
              <ShieldAlert className="h-4 w-4 text-[#ff7878] shrink-0 mt-0.5" />
              <p className="text-xs text-[#ffb3b3] leading-relaxed">
                Access denied. The CRM is restricted to <span className="font-semibold">@holdstill.in</span> team
                accounts. Please sign in with your Holdstill Workspace email.
              </p>
            </div>
          )}
          <Button
            onClick={signIn}
            data-testid="crm-google-signin-button"
            className="btn-lift glow-lime mt-8 w-full h-12 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" />
            </svg>
            Continue with Google
          </Button>
          <button
            onClick={() => navigate("/")}
            data-testid="crm-back-to-site-link"
            className="mt-6 flex items-center gap-2 text-xs text-white/40 hover:text-[color:var(--hs-lime)] mx-auto"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to holdstill.in
          </button>
        </div>
      </div>
    </div>
  );
}
